import React from 'react';
import { Outlet } from 'react-router-dom';
import { Bell, Menu } from 'lucide-react';
import { Sidebar } from '../components/Sidebar';
import ParticleCanvas from '../components/ParticleCanvas';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { CommandPaletteProvider } from '../context/CommandPaletteContext';

export function DashboardLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isLg, setIsLg] = React.useState(false);

  const sidebarWidth = isLg ? (sidebarCollapsed ? 72 : 240) : 0;
  const { permission, subscribed, loading, subscribe, sendTest } = usePushNotifications();

  React.useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const sync = () => {
      setIsLg(mq.matches);
      if (mq.matches) setMobileMenuOpen(false);
    };
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  React.useEffect(() => {
    if (!isLg && mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isLg, mobileMenuOpen]);

  return (
    <CommandPaletteProvider>
    <div className="relative min-h-screen overflow-x-hidden">
      <a
        href="#odc-main-content"
        className="fixed left-4 top-4 z-[300] -translate-y-16 rounded-lg border px-3 py-2 text-xs font-bold opacity-0 transition focus:translate-y-0 focus:opacity-100"
        style={{
          background: 'var(--bg2)',
          borderColor: 'rgba(190,81,3,0.5)',
          color: 'var(--text)',
        }}
      >
        Skip to main content
      </a>
      <ParticleCanvas />
      <div className="odc-grid-bg" aria-hidden />
      <div
        className="odc-orb"
        style={{
          width: 400,
          height: 400,
          top: '-10%',
          right: '-5%',
          background: 'rgba(190,81,3,0.08)',
          animation: 'orbFloat 10s ease-in-out infinite',
        }}
        aria-hidden
      />
      <div
        className="odc-orb"
        style={{
          width: 280,
          height: 280,
          bottom: '-5%',
          left: '-5%',
          background: 'rgba(6,148,148,0.05)',
          animation: 'orbFloat 13s ease-in-out infinite reverse',
        }}
        aria-hidden
      />
      <div className="relative z-10 flex min-h-screen min-w-0">
        {!isLg && mobileMenuOpen ? (
          <button
            type="button"
            aria-label="Close menu"
            className="fixed inset-0 z-[55] bg-black/55 backdrop-blur-[2px] lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        ) : null}
        <Sidebar
          collapsed={isLg ? sidebarCollapsed : false}
          onToggle={() => setSidebarCollapsed((v) => !v)}
          isDesktop={isLg}
          mobileOpen={mobileMenuOpen}
          onMobileClose={() => setMobileMenuOpen(false)}
        />
        <main
          className="odc-main min-w-0 flex-1 overflow-x-hidden p-4 transition-[margin-left] duration-300 sm:p-6 lg:p-8"
          style={{ marginLeft: isLg ? `${sidebarWidth}px` : 0 }}
        >
          <div className="mb-4 flex flex-wrap items-center gap-2 lg:justify-end">
            <button
              type="button"
              className="flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-bold lg:hidden"
              style={{
                borderColor: 'var(--border)',
                background: 'var(--card)',
                color: 'var(--text)',
              }}
              aria-expanded={mobileMenuOpen}
              aria-controls="odc-sidebar-nav"
              onClick={() => setMobileMenuOpen((o) => !o)}
            >
              <Menu size={18} />
              {mobileMenuOpen ? '✕ Close' : '☰ Menu'}
            </button>
            <div className="flex flex-1 flex-wrap items-center justify-end gap-2 sm:flex-none">
              <button
                type="button"
                title={`Notifications (${permission})`}
                style={{
                  padding: '8px 10px',
                  borderRadius: 10,
                  border: '1px solid var(--border)',
                  background: 'var(--card)',
                  color: 'var(--text)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Bell size={16} />
              </button>
              {!subscribed ? (
                <button
                  type="button"
                  onClick={() => void subscribe()}
                  disabled={loading}
                  title="Enable push notifications"
                  style={{
                    padding: '8px 14px',
                    borderRadius: 10,
                    background: 'rgba(190,81,3,0.1)',
                    border: '1px solid rgba(190,81,3,0.3)',
                    color: '#E8721A',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  🔔 {loading ? 'Enabling...' : 'Enable Alerts'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => void sendTest()}
                  title="Send test notification"
                  style={{
                    padding: '8px 14px',
                    borderRadius: 10,
                    background: 'rgba(6,148,148,0.1)',
                    border: '1px solid rgba(6,148,148,0.3)',
                    color: '#069494',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  ✅ Alerts On
                </button>
              )}
            </div>
          </div>
          <div id="odc-main-content" tabIndex={-1} className="outline-none">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
    </CommandPaletteProvider>
  );
}
